/**
 * ProfileEditor Component
 * 
 * Form for editing user profile information.
 * Allows users to update their name, phone number, and other profile details.
 * 
 * Requirements: 8.1
 */

import React, { useState } from 'react';
import type { User } from '../types/auth.types';
import { ErrorAlert } from './ErrorAlert';
import { LoadingSpinner } from './LoadingSpinner';

export interface ProfileEditorProps {
  user: User;
  onSubmit: (updates: Partial<User>) => Promise<void>;
  onCancel?: () => void;
}

export const ProfileEditor: React.FC<ProfileEditorProps> = ({
  user,
  onSubmit,
  onCancel,
}) => {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || '');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!firstName.trim()) {
      newErrors.push('First name is required');
    }

    if (!lastName.trim()) {
      newErrors.push('Last name is required');
    }

    // Phone number is optional, but if provided, should be valid format
    if (phoneNumber && !/^\+?[\d\s\-()]+$/.test(phoneNumber)) {
      newErrors.push('Please enter a valid phone number');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);
    setShowSuccess(false);

    try {
      const updates: Partial<User> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: `${firstName.trim()} ${lastName.trim()}`,
        phoneNumber: phoneNumber.trim() || undefined,
      };

      await onSubmit(updates);
      setShowSuccess(true);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Profile update failed']);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.7 : 1,
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#95a5a6',
    marginLeft: '8px',
  };

  return (
    <form onSubmit={handleSubmit} className="profile-editor-form">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}
      
      {showSuccess && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
            marginBottom: '16px',
          }}
        >
          Profile updated successfully!
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="email" style={labelStyle}>
          Email Address
        </label>
        <input
          id="email"
          type="email"
          value={user.email}
          disabled
          style={{
            ...inputStyle,
            backgroundColor: '#f5f5f5',
            cursor: 'not-allowed',
          }}
          aria-label="Email address (read-only)"
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Email cannot be changed
        </small>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="first-name" style={labelStyle}>
          First Name
        </label>
        <input
          id="first-name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={isLoading}
          autoComplete="given-name"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="last-name" style={labelStyle}>
          Last Name
        </label>
        <input
          id="last-name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={isLoading}
          autoComplete="family-name"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="phone-number" style={labelStyle}>
          Phone Number (Optional)
        </label>
        <input
          id="phone-number"
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={isLoading}
          autoComplete="tel"
          style={inputStyle}
          placeholder="+1 (555) 123-4567"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            style={cancelButtonStyle}
          >
            Cancel
          </button>
        )}
        <button type="submit" disabled={isLoading} style={buttonStyle}>
          {isLoading ? <LoadingSpinner size="small" /> : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};
